'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import { CartItemGifts as CoreCartItemGifts } from '@eliadmin/core/src/components/cart/detail/cart-item-utils/cart-gifts';
import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import { useEffect, useId, useState } from 'react';

import type { CartItemType } from '@eliadmin/core/src/api/cart/cart-types';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import { CartShipmentDateDialog } from '@eliadmin/core/src/components/cart/detail/shipment-types/cart-shipment-date-dialog';
import { NotInStockDialog } from '@eliadmin/core/src/components/product/dialog/not-in-stock-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { useCartActions } from '@eliadmin/core/src/hooks/cart/use-cart-actions';
import { useDebouncedFn } from '@eliadmin/core/src/hooks/use-debounce';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getDateString } from '@eliadmin/core/src/utils/misc-utils';
import { formatPrice, roundPrice } from '@eliadmin/core/src/utils/price-utils';
import {
  isSellable as isSellableFn,
  getProductMinQuantity,
  getProductQuantityStep,
  normalizeProductQuantity,
  normalizeProductQuantityDown,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutationState, useQuery } from '@tanstack/react-query';
import { FiCalendar, FiMinus, FiPlus, FiTrash2 } from 'react-icons/fi';
import { addBusinessDays, format } from 'date-fns';
import { Paragraph } from '../../ui/paragraph';
import { Heading } from '../../ui/heading';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';

export type CartItemProps = {
  item: CartItemType;
};

export function CartItem({ item }: CartItemProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);

  const { t } = useTranslations();

  const { data: productDetail } = useQuery({
    queryKey: [item.id],
    queryFn: () =>
      fetchSlugDetail({ slug: item?.slug ?? '', locale }).then(res => res['App\\Models\\Product']),
  });

  return (
    <>
      <div className="hidden w-full items-center justify-start gap-6 rounded-[4px] bg-[#F9FAFB] p-3 pr-6 lg:flex">
        <div className="flex min-w-0 flex-1 items-center justify-start gap-6">
          <div className="relative max-h-[130px] min-h-[130px] min-w-[130px] max-w-[130px] overflow-hidden rounded-[4px] border border-border bg-[#F4F4F4]">
            <Image
              src={item?.image?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(item?.image, item?.name)}
              fill
              className="bg-[#F4F4F4] object-contain object-center p-1"
            />
          </div>

          <div className="flex w-full flex-1 flex-col items-start justify-start gap-2">
            <Paragraph size="lg" color="black">
              {item.name}
            </Paragraph>
            <Paragraph size="base" color="muted-foreground">
              {t('text228')}&nbsp;<strong className="font-bold">{item.code}</strong>
            </Paragraph>
          </div>
        </div>
        <Quantity item={item} productDetail={productDetail} className="shrink-0" />
        <Paragraph size={'base'} color="black" className="w-[150px] shrink-0 font-bold xl:w-[200px]">
          {formatPrice(roundPrice(item.price), currency, currencySettings)}
        </Paragraph>
        <Paragraph size={'base'} color="black" className="w-[150px] shrink-0 font-bold xl:w-[200px]">
          {formatPrice(roundPrice(item.qty * item.price), currency, currencySettings)}
        </Paragraph>
        <DeleteOrder item={item} productDetail={productDetail} className="shrink-0" />
      </div>
      <div className="flex w-full items-stretch justify-between gap-3 rounded-[4px] bg-[#F9FAFB] p-4 sm:gap-4 md:gap-6 lg:hidden">
        <div className="flex w-full min-w-[90px] max-w-[90px] flex-col items-start justify-start gap-3 sm:min-w-[124px] sm:max-w-[124px]">
          <Image
            src={item?.image?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(item?.image, item?.name)}
            width={0}
            height={0}
            className="max-h-[90px] min-h-[90px] min-w-[90px] max-w-[90px] rounded-[4px] border border-border bg-white object-contain object-center p-2 sm:max-h-[124px] sm:min-h-[124px] sm:min-w-[124px] sm:max-w-[124px]"
            sizes="50vw"
          />
          <Quantity item={item} productDetail={productDetail} />
        </div>
        <div className="flex w-full flex-col items-start justify-between gap-2">
          <div className="flex w-full items-start justify-between gap-3">
            <div className="flex w-full flex-col items-start justify-start gap-1">
              <Heading tag="h6" title={item.name} />
              <Paragraph size="base" color="muted-foreground">
                {item.code}
              </Paragraph>
            </div>
            <DeleteOrder item={item} productDetail={productDetail} />
          </div>
          <div className="flex w-full items-start justify-start gap-4">
            <div className="flex flex-col items-start justify-start gap-1">
              <Paragraph size="base" color="muted-foreground" className="font-bold">
                {t('text662')}
              </Paragraph>
              <p className={cn('h6', 'font-semibold tracking-tight text-black')}>
                {formatPrice(roundPrice(item.price), currency, currencySettings)}
              </p>
            </div>
            <div className="flex flex-col items-start justify-start gap-1">
              <Paragraph size="base" color="muted-foreground" className="font-bold">
                {t('text482')}
              </Paragraph>
              <p className={cn('h6', 'font-semibold tracking-tight text-black')}>
                {formatPrice(roundPrice(item.qty * item.price), currency, currencySettings)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export type CartItemSegment = {
  item: CartItemType;
  className?: string;
  productDetail?: ProductDetailData;
  label?: boolean;
};

export function CartItemTimeDelivery(props: CartItemProps) {
  const { t } = useTranslations();

  const { updateCartMutation } = useCartActions();
  const openDialog = useDialogStore(s => s.open);
  const locale = useLocaleStore(s => s.currentLocale);

  const [note, setNote] = useState<string | null>(props.item.note);
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(
    props.item.time_of_delivery ? new Date(props.item.time_of_delivery) : undefined,
  );

  const baseId = useId();
  const dateId = `${baseId}-date`;
  const noteId = `${baseId}-note`;

  const handlePickShipmentDateClick = () => {
    openDialog({
      id: CartShipmentDateDialog.id,
      content: (
        <CartShipmentDateDialog
          pickDate={date => {
            setSelectedDay(date);
            update(note, date);
          }}
          selectedDay={selectedDay}
        />
      ),
    });
  };

  const update = (note: string | null, date: Date | undefined) => {
    updateCartMutation.mutate({
      hash: props.item.hash,
      item: {
        sub_items: props.item.sub_items,
        product: props.item.product,
        variant: props.item.variant,
      },
      time_of_delivery: date ? getDateString(date) : undefined,
      note: note ?? undefined,
      qty: 1,
    });
  };

  return (
    <div className="flex w-full max-w-[750px] flex-col items-start gap-4 md:flex-row ">
      <div className="flex w-full flex-1 flex-col items-start gap-1">
        <Label htmlFor={dateId}>{t('text727')}</Label>
        <Input
          id={noteId}
          placeholder={t('text727')}
          value={note ?? ''}
          onChange={e => setNote(e.target.value)}
          onBlur={() => update(note, selectedDay)}
          className={{ wrapper: 'w-full' }}
        />
      </div>
      <div className="flex w-full flex-1 flex-col items-start gap-1">
        <Label htmlFor={dateId}>{t('text708')}</Label>
        <Input
          id={dateId}
          placeholder={t('text708')}
          className={{ wrapper: 'w-full cursor-pointer', main: 'cursor-pointer' }}
          readOnly
          value={selectedDay ? selectedDay.toLocaleDateString(locale) : ''}
          onClick={handlePickShipmentDateClick}
          after={
            <Button
              size="icon-sm"
              variant="ghost"
              className="mr-2 hover:bg-walnut-soft hover:text-walnut"
              onClick={handlePickShipmentDateClick}
            >
              <FiCalendar />
            </Button>
          }
        />
      </div>
    </div>
  );
}

export type CartItemGiftsProps = {
  className?: string;
  item: CartItemType;
};

export function CartItemGifts({ item, className }: CartItemGiftsProps) {
  return <CoreCartItemGifts item={item} className={className} />;
}

export function DeleteOrder(props: CartItemSegment) {
  const { t } = useTranslations();
  const { deleteCartMutation } = useCartActions();

  function handleDelete() {
    deleteCartMutation.mutate({ hash: props.item.hash });
    // analytics
    if (window.ga) {
      window.ga('ec:addProduct', {
        id: props.item.id,
        name: props.item.name,
        // brand: $(this).data('manufacturer'),
        // category: props.item.
        price: props.item.price,
        quantity: props.item.qty,
      });
      window.ga('ec:setAction', 'remove');
      window.ga('send', 'event', 'UX', 'click', 'remove from cart');
    }

    if (window.dataLayer) {
      window.dataLayer.push({ ec: null });
      window.dataLayer.push({
        event: 'removeFromCart',
        ec: {
          currencyCode: useGlobalStore.getState().generalLayout.currency,
          remove: {
            products: [
              {
                id: props.item.id,
                name: props.item.name,
                // brand: $(this).data('manufacturer'),
                // category: props.item.
                price: props.item.price,
                quantity: props.item.qty,
              },
            ],
          },
        },
      });
    }
  }

  return (
    <div className={cn('flex items-center justify-center', props.className)}>
      <Button
        className="max-h-6 min-h-6 transition-all hover:opacity-75 sm:min-w-6 sm:max-w-6"
        onClick={handleDelete}
        isLoading={deleteCartMutation.isPending}
        size="none"
        variant={'none'}
        aria-label={t('text1611')}
      >
        <FiTrash2 size={24} className="text-[#666666]" aria-hidden />
      </Button>
    </div>
  );
}

export function Quantity({ item, className }: CartItemSegment) {
  const { t } = useTranslations();
  const { updateQuantityMutation } = useCartActions();
  const openModal = useDialogStore(s => s.open);

  const [internalValue, setInternalValue] = useState<string>(String(item.qty));
  const debouncedMutate = useDebouncedFn(updateQuantityMutation.mutate, 1000);
  const minQuantity = getProductMinQuantity(item);
  const step = getProductQuantityStep(item);

  const openNotInStockDialog = () => {
    openModal({
      id: NotInStockDialog.id,
      content: <NotInStockDialog maxQuantity={item.stock} unit={item.unit} />,
    });
  };

  useEffect(() => {
    setInternalValue(String(item.qty));
  }, [item.qty]);

  const disabled = item.delivery_by_date;

  const isDesktop = useBreakpoint('sm');

  return (
    <div
      className={cn(
        'flex w-full max-w-[90px] items-center justify-center gap-1 rounded-[4px] border border-border bg-white sm:max-w-[124px] sm:gap-2 sm:px-6',
        disabled && 'pointer-events-none cursor-not-allowed opacity-50',
        className,
      )}
    >
      <Button
        variant="none"
        size="none"
        className="rounded-full bg-transparent"
        disabled={item.qty <= minQuantity}
        aria-label={`${t('text22')} −`}
        onClick={() => {
          const internalNumValue = Number(internalValue);
          if (!isNaN(internalNumValue) && internalNumValue > minQuantity) {
            setInternalValue(String(normalizeProductQuantity(internalNumValue - step, item)));
            debouncedMutate({
              hash: item.hash,
              qty: normalizeProductQuantity(internalNumValue - step, item),
            });
          }

          if (internalNumValue <= minQuantity) {
            setInternalValue(String(minQuantity));
          }
        }}
      >
        <div>
          <FiMinus size={isDesktop ? 20 : 18} className={'text-primary'} aria-hidden />
        </div>
      </Button>
      <Input
        className={{
          wrapper:
            'max-h-8 min-h-8 min-w-5 max-w-5 !border-none !bg-transparent !p-0 sm:max-h-12 sm:min-h-12',
          main: 'max-h-8 min-h-8 min-w-5 max-w-5 !border-none !bg-transparent !p-0 text-center text-sm font-semibold text-black sm:max-h-12 sm:min-h-12 lg:text-base',
        }}
        value={internalValue}
        inputMode="numeric"
        aria-label={t('text22')}
        onChange={e => {
          const strValue = e.target.value;
          setInternalValue(strValue);

          if (!strValue) return;

          const numValue = Number(strValue);
          if (isNaN(numValue)) return;

          if (
            numValue >= minQuantity &&
            (numValue <= item.stock ||
              isSellableFn({ ...item, quantity: item.qty } as any, numValue))
          ) {
            debouncedMutate({ hash: item.hash, qty: normalizeProductQuantity(numValue, item) });
          }

          // if (numValue > item.stock) {
          //   openNotInStockDialog();
          //   setInternalValue(String(normalizeProductQuantityDown(item.stock, item)));
          //   return;
          // }

          // if (numValue < minQuantity) {
          //   setInternalValue(String(minQuantity));
          //   return;
          // }
        }}
        onBlur={() => {
          const numValue = Number(internalValue);
          if (isNaN(numValue)) {
            setInternalValue(String(item.qty));
          }

          if (numValue < minQuantity) {
            setInternalValue(String(minQuantity));
          }

          if (
            numValue > item.stock &&
            !isSellableFn({ ...item, quantity: item.qty } as any, numValue)
          ) {
            openNotInStockDialog();
            setInternalValue(String(normalizeProductQuantityDown(item.stock, item)));
          }
        }}
      />
      <Button
        variant="none"
        size="none"
        className="rounded-full bg-transparent"
        // Not disabled at stock: made-to-order items are sellable with 0 stock, and the handler
        // below already opens the "not in stock" dialog when the quantity is not sellable.
        aria-label={`${t('text22')} +`}
        onClick={() => {
          const internalNumValue = Number(internalValue);

          if (
            internalNumValue >= item.stock &&
            !isSellableFn({ ...item, quantity: item.qty } as any, internalNumValue)
          ) {
            return openNotInStockDialog();
          }

          if (
            !isNaN(internalNumValue) &&
            (internalNumValue < item.stock ||
              isSellableFn({ ...item, quantity: item.qty } as any, internalNumValue))
          ) {
            setInternalValue(String(normalizeProductQuantity(internalNumValue + step, item)));
            debouncedMutate({
              hash: item.hash,
              qty: normalizeProductQuantity(internalNumValue + step, item),
            });
          }
        }}
      >
        <div>
          <FiPlus size={isDesktop ? 20 : 18} className={'text-primary'} aria-hidden />
        </div>
      </Button>
    </div>
  );
}

export function UnitPrice({ item, label, className }: CartItemSegment) {
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);

  return (
    <div
      className={cn(
        'flex flex-col items-start justify-center font-bold md:items-center',
        className,
      )}
    >
      {label && <span className="text-sm font-semibold text-muted-foreground">{t('text663')}</span>}
      {formatPrice(
        roundPrice(
          templateSettings?.product_detail?.price_without_vat_first?.enabled
            ? item.price_without_vat
            : item.price,
        ),
        currency,
        currencySettings,
      )}
      /{item.unit}
    </div>
  );
}
export function TotalPrice({ item, label, className }: CartItemSegment) {
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);

  return (
    <div
      className={cn(
        'flex flex-col items-start justify-center font-bold md:items-center',
        className,
      )}
    >
      {label && <span className="text-sm font-semibold text-muted-foreground">{t('text663')}</span>}
      {formatPrice(
        roundPrice(
          templateSettings?.product_detail?.price_without_vat_first?.enabled
            ? item.price_subtotal_without_vat
            : item.price_subtotal,
        ),
        currency,
        currencySettings,
      )}
    </div>
  );
}

export function CartItemStockCustom({ className, item }: CartItemSegment) {
  const { t } = useTranslations();
  const determinedAvailability = item.stock >= item.qty ? item.availability : item.unavailability;

  // determine if this item is sellable
  const isSellable =
    (item.stock > 0 && item.stock >= item.qty) || Boolean(determinedAvailability?.sellable);

  // choose the business‐day range
  const [minDays, maxDays] = isSellable ? [2, 3] : [5, 8];

  // compute the start and end dates, skipping weekends
  const startDate = addBusinessDays(new Date(), minDays);
  const endDate = addBusinessDays(new Date(), maxDays);

  // format as e.g. "Apr 22" or include year if you prefer: "Apr 22, 2025"
  const formattedStart = format(startDate, 'dd.M');
  const formattedEnd = format(endDate, 'dd.M');

  return (
    <div className={cn('flex flex-col items-start justify-center', className)}>
      <div
        className={cn(
          'mt-1 text-sm text-gray-600',
          isSellable ? 'text-success-foreground' : 'text-destructive',
        )}
      >
        Doručenie{' '}
        <span className="whitespace-nowrap font-bold">
          {formattedStart} – {formattedEnd}
        </span>{' '}
        {t('text2298')}
      </div>
    </div>
  );
}

export function ItemInfo({ item, productDetail, className }: CartItemSegment) {
  const templateSettings = useGlobalStore(s => s.templateSettings);

  return (
    <div className={cn('flex flex-col gap-1 md:min-w-[200px]', className)}>
      {!!(productDetail?.discount && productDetail?.discount > 0) && (
        <div className="flex h-6 w-[50px] items-center justify-center rounded-none bg-[#DE3618]/10 text-sm text-[#DE3618]">
          -{productDetail.discount.toFixed(0)}%
        </div>
      )}
      <Link
        href={item?.slug ?? '#'}
        className={!(productDetail?.discount && productDetail?.discount > 0) ? 'mt-4' : ''}
        prefetch={false}
      >
        <h6 className="text-base font-bold">{item.name}</h6>
      </Link>
      {!!item.sub_items.packages?.length && (
        <div className="text-sm text-muted-foreground">
          {item.sub_items.packages
            .filter(p => p.selected)
            .map(p => p.name)
            .join(', ')}
        </div>
      )}
      {!!templateSettings?.cart?.show_cart_item_attributes?.enabled &&
        item.attributes?.map((attr, i) => (
          <span key={i} className="text-sm text-muted-foreground">
            {attr.attribute_name}: {attr.custom_value ? attr.custom_value : ''}
            {attr.name}
          </span>
        ))}
      {/* <small className="text-muted-foreground">
        {t('text19')}: {item.code}
      </small> */}
      {/* <span className="attribute">Product attribute</span> */}
    </div>
  );
}

export function ItemImage({ item, className }: CartItemSegment) {
  return (
    <Image
      alt={resolveImageAlt(item?.image, item?.name)}
      src={item.image.url}
      width="0"
      height="0"
      sizes="100vw"
      className={cn('h-full w-full rounded-lg object-contain', className)}
    />
  );
}
